import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { NodeRunInstance, Run, RunInstance, RunsAggregation, WorkflowOutput } from "../types/workflow";
import customFetch from "../utils/customFetch";
import { queryKeys } from "../queryKeys";
import queryClient from "../queryClient";
import { useWorkflowId } from "../utils/misc";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { RunState } from "../constants/workflow";
import { method } from "lodash";

export const useGenerateWorkflows = () => {
  return useMutation<WorkflowOutput, ErrorEvent, { prompt: string }>({
    mutationFn: ({ prompt }) => {
      return customFetch(`/v1/generateWorkflow/`, {
        method: 'POST',
        body: JSON.stringify({ prompt: prompt })
      })
        .then(async(res) => {
          if (res.status === 401) {
            return Promise.reject(new Error('401: Authentication failed. Please log in again.'));
          }
          
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to generate workflow: ${res.status}`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    }
  });
}

export const useGetAllWorkflows = () => {
  return useQuery<WorkflowOutput[], ErrorEvent>({
    queryKey: queryKeys.allWorkflows(),
    queryFn: () => {
      return customFetch(`/v1/workflows`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get workflows'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useGetWorkflowById = ({workflowId}: { workflowId: string }) => {
  return useQuery<WorkflowOutput, ErrorEvent>({
    queryKey: ['workflow', workflowId],
    queryFn: () => {
      return customFetch(`/v1/workflows/${workflowId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get workflow'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useCheckWorkflowHasNodes = (workflowId: string) => {
  return useQuery<{has_nodes: boolean}, ErrorEvent>({
    queryKey: ['workflow', workflowId, 'has-nodes'],
    queryFn: () => {
      return customFetch(`/v1/workflows/${workflowId}/has-nodes`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to check if workflow has nodes'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useDeleteWorkflowById = () => {
  return useMutation<null, ErrorEvent, { workflowId: string, hideToast?: boolean }>({
    mutationFn: ({ workflowId, hideToast }) => {
      return customFetch(`/v1/workflows/${workflowId}`, {
        method: "DELETE"
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          if (!hideToast) {
            toast.success("Workflow deleted successfuly");
          }
          return data;
        }
        return Promise.reject(new Error(`Failed to delete workflow with id ${workflowId}`))
      })
      .catch((err) => toast.error(err.message));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.allWorkflows()});
    },
  });
}

export const useCloneWorkflow = () => {
  return useMutation<WorkflowOutput, ErrorEvent, { workflowId: string }>({
    mutationFn: async ({ workflowId }) => {
      return customFetch(`/v1/workflows/${workflowId}/duplicate`, { method: 'GET' })
        .then(async (res) => {
          const data = await res.json();
          return res.ok ? data : Promise.reject(new Error(data?.message || 'Failed to duplicate workflow'))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allWorkflows() });
    }
  });
}

export const useTransferWorkflow = () => {
  return useMutation<{ workflow: WorkflowOutput, transferred_to_user: string, message: string }, ErrorEvent, { workflowId: string, targetEmail: string }>({
    mutationFn: async ({ workflowId, targetEmail }) => {
      return customFetch(`/v1/admin/workflows/transfer`, { 
        method: 'POST',
        body: JSON.stringify({
          workflow_id: workflowId,
          target_email: targetEmail
        })
      })
        .then(async (res) => {
          const data = await res.json();
          if (res.ok) {
            toast.success(`Workflow transferred successfully to ${targetEmail}`);
            return data;
          }
          return Promise.reject(new Error(data?.message || 'Failed to transfer workflow'))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allWorkflows() });
    }
  });
}

export const useGetRunsForWorkflow = (runId?: string) => {
  const workflowId = useWorkflowId();
  const {page, limit, offset, stateid} = usePaginationValues();
  return useQuery<Run[], ErrorEvent>({
    queryKey: [...queryKeys.workflowRuns(workflowId), page, limit, stateid, runId],
    queryFn: () => {
      let url = `/v1/runs?workflow=${workflowId}`;
      if (runId && runId.trim() !== '') {
        url += `&runId=${encodeURIComponent(runId.trim())}`;
      } else {
        url += `&limit=${limit}&offset=${offset}&state=${stateid}`;
      }
      return customFetch(url)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get run history'))
        })
        .catch((err) => toast.error(err.message));
    },
    enabled: !!workflowId,
    staleTime: 0,
  });
}


export const useGetRunsCountForWorkflow = () => {
  const workflowId = useWorkflowId();
  const {page, limit, offset, stateid} = usePaginationValues();
    
  return useQuery<RunsAggregation, ErrorEvent>({
    queryKey: [...queryKeys.workflowRunsCount(workflowId), page, limit, stateid],
    queryFn: () => {
      return customFetch(`/v1/runscount?workflow=${workflowId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get run history count'))
        })
        .catch((err) => toast.error(err.message));
    },
    enabled: !!workflowId,
    staleTime: 0,
  });
}

export const useGetRunById = (runId: string) => {
  return useQuery<Run, ErrorEvent>({
    queryKey: ['run', runId],
    queryFn: () => {
      return customFetch(`/v1/runs/${runId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get run'))
        })
        .catch((err) => toast.error(err.message));
    },
    enabled: !!runId,
  });
}

export const useGetRunInstancesForRun = (runId: string) => {
  return useQuery<RunInstance[], ErrorEvent>({
    queryKey: ['runInstances', runId],
    queryFn: () => {
      return customFetch(`/v1/runinstances/?run=${runId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get run instances'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useGetNodeRunInstancesForRunInstance = () => {
  return useMutation<NodeRunInstance[], ErrorEvent, { runInstanceId: string }>({
    mutationFn: ({ runInstanceId }) => {
      return customFetch(`/v1/noderuninstances?runinstance=${runInstanceId}`)
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to get node run instances'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useCreateWorkflow = () => {
  const {refetch: refetchWorkflows} = useGetAllWorkflows();
  return useMutation<WorkflowOutput, ErrorEvent>({
    mutationFn: () => {
      const payload = {
        name: 'New Workflow'
      }
      return customFetch(`/v1/workflows`, {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      .then(async(res) => {
        if (res.status === 401) {
          return Promise.reject(new Error('401: Authentication failed. Please log in again.'));
        }
        
        const data = await res.json();
        refetchWorkflows();
        return res.ok
          ? data
          : Promise.reject(new Error(`Failed to create workflow: ${res.status}`))
      })
      .catch((err) => {
        toast.error(err.message);
        throw err;
      });
    }
  });
}

export const useDeleteRunById = () => {
  const workflowId = useWorkflowId();
  return useMutation<null, ErrorEvent, { runId: string }>({
    mutationFn: ({ runId }) => {
      return customFetch(`/v1/runs/${runId}`, {
        method: "DELETE"
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success("Run deleted successfuly");
          return data;
        }
        return Promise.reject(new Error(`Failed to delete run with id ${runId}`))
      })
      .catch((err) => toast.error(err.message));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.workflowRuns(workflowId)});
    },
  });
}

export const useRetryRun = () => {
  const workflowId = useWorkflowId();
  return useMutation<Run, ErrorEvent, { runId: string; mode: 'whole' | 'failed_nodes' }>({
    mutationFn: ({ runId, mode }) => {
      return customFetch(`/v1/runs/${runId}/retry?mode=${mode}`, {
        method: "POST",
      })
        .then(async (res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(data?.message || 'Failed to retry run'));
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: (run, { runId }) => {
      // Refresh run list and counts
      queryClient.invalidateQueries({ queryKey: queryKeys.workflowRuns(workflowId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.workflowRunsCount(workflowId) });
      // Refresh this run's details and its run instances so the new instance appears in the dropdown
      queryClient.invalidateQueries({ queryKey: ['run', runId] });
      queryClient.invalidateQueries({ queryKey: ['runInstances', runId] });
    },
  });
}

export const useUpdateWorkflow = () => {
  return useMutation<
    WorkflowOutput, 
    ErrorEvent, 
    { workflowId: string, workflow: WorkflowOutput },
    { previousWorkflow?: WorkflowOutput, previousWorkflows?: WorkflowOutput[] }
  >({
    mutationFn: ({ workflowId, workflow }) => {
      return customFetch(`/v1/workflows/${workflowId}`, {
        method: 'PATCH',
        body: JSON.stringify(workflow)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to update workflow'))
      })
      .catch((err) => toast.error(err.message));
    },
    onMutate: async ({ workflowId, workflow }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['workflow', workflowId] });
      await queryClient.cancelQueries({ queryKey: queryKeys.allWorkflows() });

      // Snapshot the previous values
      const previousWorkflow = queryClient.getQueryData<WorkflowOutput>(['workflow', workflowId]);
      const previousWorkflows = queryClient.getQueryData<WorkflowOutput[]>(queryKeys.allWorkflows());

      // Optimistically update the workflow
      if (previousWorkflow) {
        queryClient.setQueryData<WorkflowOutput>(['workflow', workflowId], {
          ...previousWorkflow,
          ...workflow
        });
      }

      // Optimistically update the workflows list
      if (previousWorkflows) {
        queryClient.setQueryData<WorkflowOutput[]>(
          queryKeys.allWorkflows(),
          previousWorkflows.map(w => w.id === workflowId ? { ...w, ...workflow } : w)
        );
      }

      return { previousWorkflow, previousWorkflows };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      const typedContext = context as { previousWorkflow?: WorkflowOutput, previousWorkflows?: WorkflowOutput[] } | undefined;
      if (typedContext?.previousWorkflow) {
        queryClient.setQueryData(['workflow', variables.workflowId], typedContext.previousWorkflow);
      }
      if (typedContext?.previousWorkflows) {
        queryClient.setQueryData(queryKeys.allWorkflows(), typedContext.previousWorkflows);
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['workflow', variables.workflowId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.allWorkflows() });
    }
  });
}

export const usePaginationValues = (): { page: number, limit: number, state: string, offset: number, stateid : RunState } => {
  const [searchParams] = useSearchParams();
  const defaultPage = 1;
  const defaultPageSize = 20;
  //to fetch from queryparams
  const pagequery = parseInt(searchParams.get('page') || '');
  const limitquery = parseInt(searchParams.get('limit') || '');
  const state = searchParams.get('state') || 'all';
  let stateid = -1;
  switch(state) {
    case 'waiting': 
      stateid = RunState.RunPending;
      break;
    case 'completed':
      stateid = RunState.RunCompleted;
      break;
    case 'filtered':
      stateid = RunState.RunFiltered;
      break;
    case 'failed':
      stateid = RunState.RunFailed;
      break;
  }

  //to check if invalid number
  const page = isNaN(pagequery) ? defaultPage : pagequery;
  const limit = isNaN(limitquery) ? defaultPageSize : limitquery;

  //calculate offset for query
  const offset = (isNaN(page) ? 1 : page - 1) * (isNaN(limit) ? defaultPageSize : limit);

  return { page, limit, state, offset, stateid };
}

export const usePublishWorkflow = () => {
  return useMutation<WorkflowOutput, ErrorEvent, { workflowId: string }>({
    mutationFn: ({ workflowId }) => {
      return customFetch(`/v1/workflows/${workflowId}/publish`, {
        method: 'POST'
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success("Workflow published successfully");
          return data;
        }
        return Promise.reject(new Error('Failed to publish workflow'))
      })
      .catch((err) => toast.error(err.message));
    },
    onMutate: async ({ workflowId }) => {
      // Optimistically set unpublished changes to false immediately
      queryClient.setQueryData(
        ['workflow', workflowId, 'has-unpublished-changes'],
        { has_unpublished_changes: false }
      );
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({queryKey: queryKeys.allWorkflows()});
      // Refetch to ensure we have the correct status from server
      await queryClient.refetchQueries({queryKey: ['workflow', variables.workflowId, 'has-unpublished-changes']});
    },
    onError: (_, variables) => {
      // On error, invalidate to refetch the correct status
      queryClient.invalidateQueries({queryKey: ['workflow', variables.workflowId, 'has-unpublished-changes']});
    },
  });
}

export const useTurnWorkflowOnOrOff = () => {
  return useMutation<WorkflowOutput, ErrorEvent, { workflowId: string, switchedOff: boolean }>({
    mutationFn: ({ workflowId, switchedOff }) => {
      return customFetch(`/v1/workflows/${workflowId}/switch-off`, {
        method: 'POST',
        body: JSON.stringify({ switched_off: switchedOff })
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success(`Workflow ${switchedOff ? 'disabled' : 'enabled'} successfully`);
          return data;
        }
        return Promise.reject(new Error(`Failed to ${switchedOff ? 'disable' : 'enable'} workflow`))
      })
      .catch((err) => toast.error(err.message));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.allWorkflows()});
    },
  });
}

export const useCheckUnpublishedChanges = (workflowId: string) => {
  return useQuery<{has_unpublished_changes: boolean}, ErrorEvent>({
    queryKey: ['workflow', workflowId, 'has-unpublished-changes'],
    queryFn: () => {
      return customFetch(`/v1/workflows/${workflowId}/has-unpublished-changes`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to check unpublished changes'))
        })
        .catch((err) => {
          // Don't show toast for this, just log silently
          console.error(err);
          return { has_unpublished_changes: false };
        });
    },
    enabled: !!workflowId,
    staleTime: 0, // Always refetch when invalidated
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchInterval: 15000, // Auto-refresh every 15 seconds
  });
}
